import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { BackendService } from 'src/app/core/backend.service';
import { RootState } from 'src/app/root-store/root-state.model';
import { NavigateTo } from 'src/app/root-store/router/actions';
import * as UserActions from 'src/app/root-store/user-store/actions';
import { selectUser } from 'src/app/root-store/user-store/selectors';
import { IdentityResult } from '../user.interfaces';
import { RepeatPasswordEStateMatcher } from './repeat-password-e-state-matcher';

export enum UserPasswordFormMode {
  Forgot = 'forgot',
  Reset = 'reset',
  Change = 'change'
}

export interface UserPasswordDialogData {
  formMode: UserPasswordFormMode
  email: string
}

@Component({
  selector: 'app-user-password',
  templateUrl: './user-password.component.html',
  styleUrl: './user-password.component.scss',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule, TranslateModule,
    MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule
  ]
})
export class UserPasswordComponent implements OnInit {
  @ViewChild('btnSubmit') btnSubmitEl!: MatButton;
  @ViewChild('txtNewPassword') txtNewPasswordEl!: ElementRef<HTMLInputElement>;
  @ViewChild('txtCurrentPassword') txtCurrentPasswordEl!: ElementRef<HTMLInputElement>;

  pwForm = this.fb.group({
    email: [''],
    currentPassword: [''],
    newPassword: [''],
    newPasswordConfirm: ['']
  })
  pwRepeatMatcher = new RepeatPasswordEStateMatcher()

  isSubmitting = false
  formMode!: UserPasswordFormMode

  private code!: string

  constructor(
    private fb: FormBuilder,
    private store: Store<RootState>,
    private dialogRef: MatDialogRef<UserPasswordComponent>,
    private cdRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private backend: BackendService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) private dialogData: UserPasswordDialogData
  ) { }

  ngOnInit() {
    this.pwForm.addValidators(this.checkPasswords)

    this.formMode = this.dialogData.formMode

    if (this.dialogData.email) {
      this.pwForm.controls.email.setValue(this.dialogData.email)

      // try to submit form immediately, if an email address is entered
      if (this.formMode === UserPasswordFormMode.Forgot)
        setTimeout(() => this.btnSubmitEl._elementRef.nativeElement.click())
    }

    // Optionally get some values from URL
    this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
      if (params['code'])
        this.code = params['code']
      if (params['username']) {
        this.pwForm.controls.email.setValue(params['username'])
        this.cdRef.detectChanges()
      }
    })

    switch (this.formMode) {
      case UserPasswordFormMode.Reset:
        this.pwForm.controls.email.disable()
        this.txtNewPasswordEl.nativeElement.focus()
        break
      case UserPasswordFormMode.Change:
        const user = this.store.selectSignal(selectUser)()
        this.pwForm.controls.email.disable()
        this.pwForm.controls.email.setValue(user.profile!.email)
        this.cdRef.detectChanges()
        this.txtCurrentPasswordEl.nativeElement.focus()
        break;
    }
  }

  onSubmit() {
    this.isSubmitting = true;

    let result$: Observable<IdentityResult>

    switch (this.formMode) {
      case UserPasswordFormMode.Forgot:
        result$ = this.backend.forgotPassword(this.pwForm.controls.email.value!)
        break;
      case UserPasswordFormMode.Reset:
        result$ = this.backend.resetPassword({
          email: this.pwForm.controls.email.value!,
          code: this.code,
          newPassword: this.pwForm.controls.newPassword.value!
        }).pipe(map(res => res.identityResult!))
        break;
      case UserPasswordFormMode.Change:
        result$ = this.backend.changePassword({
          email: this.pwForm.controls.email.value!,
          currentPassword: this.pwForm.controls.currentPassword.value!,
          newPassword: this.pwForm.controls.newPassword.value!
        })
        break
    }

    result$
      .pipe(tap(() => this.isSubmitting = false))
      .subscribe({
        next: res => {
          if (res.succeeded) {
            let showCompletedMsg = false

            switch (this.formMode) {
              case UserPasswordFormMode.Forgot:
                this.snackBar.open(this.translate.instant('PW.RESET_LINK_SENT', { email: this.pwForm.controls.email.value }), this.translate.instant('CLOSE'), { duration: 60000 })
                break
              case UserPasswordFormMode.Change:
                showCompletedMsg = true
                break
              case UserPasswordFormMode.Reset:
                showCompletedMsg = true
                this.store.dispatch(UserActions.LoadUser())
                this.store.dispatch(NavigateTo({ route: 'Home' }))
                break
            }

            if (showCompletedMsg)
              this.snackBar.open(this.translate.instant('PW.RESET_COMPLETED'), undefined, { duration: 5000 })

            this.dialogRef.close('success')
          }
          else
            this.pwForm.setErrors({ serverError: res.errors.map(e => e.description).join('\n') })
        },
        error: error => this.pwForm.setErrors({ serverError: error })
      })
  }

  // source: https://stackoverflow.com/a/51606362/4049371
  checkPasswords(group: AbstractControl): ValidationErrors | null {
    return group.get('newPassword')?.value === group.get('newPasswordConfirm')?.value ? null : { notSame: true }
  }
}
